import { Injectable } from '@angular/core';

import type { StoredSession } from '../models/token-pair.model';

const STORAGE_KEY = 'wizcode_crm.auth';

/**
 * Persists auth tokens in localStorage so sessions survive page reloads.
 * Tokens remain client-side only; the API uses short-lived JWT access tokens.
 */
@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  load(): StoredSession | null {
    if (!this.isAvailable()) {
      return null;
    }

    const raw = localStorage.getItem(STORAGE_KEY);

    if (raw === null) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as StoredSession;

      if (
        typeof parsed.access_token !== 'string' ||
        typeof parsed.refresh_token !== 'string' ||
        typeof parsed.expires_at !== 'number'
      ) {
        this.clear();
        return null;
      }

      return parsed;
    } catch {
      this.clear();
      return null;
    }
  }

  save(session: StoredSession): void {
    if (!this.isAvailable()) {
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }

  clear(): void {
    if (!this.isAvailable()) {
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
  }

  private isAvailable(): boolean {
    return (
      typeof localStorage !== 'undefined' &&
      typeof localStorage.getItem === 'function' &&
      typeof localStorage.setItem === 'function' &&
      typeof localStorage.removeItem === 'function'
    );
  }
}
